<!--
  SalesReportList.vue

  Sales Report grid page. Uses the dedicated /stocks/sales-report-list
  endpoint which returns a fully-flattened row shape (no nested entity
  navigation in the response) plus live sums from the four payment
  buckets (Customer / Purchase / Expense / Admin Cost). The previous
  version dereferenced nested objects that the slim /stocks list endpoint
  didn't include, causing TypeErrors and a stuck "loading forever" state.
-->
<template>
  <div class="sales-report-page">
    <!-- ===== Top action bar ===== -->
    <CCard class="action-bar mb-3">
      <CCardBody class="py-3">
        <div class="d-flex justify-content-between align-items-center flex-wrap">
          <div>
            <h4 class="mb-0">
              <i class="fas fa-chart-line me-2 text-primary"></i>
              <span>Sales Report</span>
            </h4>
            <small class="text-muted">
              {{ totalItems }} sold/booked stocks
              <span v-if="loading"> · loading...</span>
            </small>
          </div>
          <div class="action-buttons">
            <CButton color="secondary" variant="outline" @click="toggleFilters" class="me-2">
              <i class="fas fa-filter me-1"></i>
              {{ filtersExpanded ? "Hide Filters" : "Show Filters" }}
            </CButton>
            <CButton color="success" @click="exportCSV" :disabled="loading || !loadedItems.length">
              <i class="fas fa-file-csv me-1"></i> Export CSV
            </CButton>
          </div>
        </div>
      </CCardBody>
    </CCard>

    <!-- ===== Filters card ===== -->
    <CCard v-if="filtersExpanded" class="filter-card mb-3">
      <CCardHeader class="section-header">
        <i class="fas fa-filter me-2"></i><strong>Filters</strong>
      </CCardHeader>
      <CCardBody>
        <CRow>
          <CCol md="4">
            <CInput
              label="Stock No"
              v-model="filters.stockNo"
              @input="onFilterChange"
              placeholder="Search stock no"
            />
          </CCol>
          <CCol md="4">
            <CInput
              label="Customer"
              v-model="filters.customerName"
              @input="onFilterChange"
              placeholder="Customer name"
            />
          </CCol>
          <CCol md="4">
            <CInput
              label="Salesman"
              v-model="filters.salesmanName"
              @input="onFilterChange"
              placeholder="Salesman name"
            />
          </CCol>
          <CCol md="4">
            <CInput
              label="Chassis No"
              v-model="filters.chasisNo"
              @input="onFilterChange"
              placeholder="Chassis number"
            />
          </CCol>
          <CCol md="4">
            <CInput
              label="Brand / Model"
              v-model="filters.brandModelName"
              @input="onFilterChange"
              placeholder="e.g. Toyota Vellfire"
            />
          </CCol>
          <CCol md="2">
            <CInput
              label="Min Price"
              type="number"
              v-model="priceMin"
              @input="onFilterChange"
              prepend="RM"
            />
          </CCol>
          <CCol md="2">
            <CInput
              label="Max Price"
              type="number"
              v-model="priceMax"
              @input="onFilterChange"
              prepend="RM"
            />
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>

    <!-- ===== Results card ===== -->
    <CCard class="results-card">
      <CCardHeader class="section-header d-flex justify-content-between align-items-center">
        <div>
          <i class="fas fa-table me-2"></i>
          <strong>Results</strong>
          <CBadge color="info" class="ms-2">{{ totalItems }}</CBadge>
        </div>
        <div class="form-check form-switch mb-0">
          <label class="form-check-label small">
            <input type="checkbox" class="form-check-input" v-model="showAllColumns" />
            Show all columns
          </label>
        </div>
      </CCardHeader>

      <CCardBody class="p-0">
        <div v-if="loading && !loadedItems.length" class="text-center py-5">
          <CSpinner color="primary" />
          <p class="text-muted mt-3">Loading sales report...</p>
        </div>

        <div v-else-if="!loading && !loadedItems.length" class="text-center py-5">
          <i class="fas fa-inbox fa-3x text-muted mb-3"></i>
          <p class="text-muted">No matching sales report rows.</p>
        </div>

        <div v-else class="table-responsive">
          <CDataTable
            :items="loadedItems"
            :fields="visibleFields"
            hover
            striped
            small
            :loading="loading"
            class="sales-report-table"
          >
            <template #show_index="{ index }">
              <td class="text-center text-muted">{{ (activePage - 1) * itemsPerPage + index + 1 }}</td>
            </template>

            <template #vehicleRegistrationDate="{ item }">
              <td>{{ formatDate(item.vehicleRegistrationDate) }}</td>
            </template>

            <template #stockNo="{ item }">
              <td><strong>{{ item.stockNo || "—" }}</strong></td>
            </template>

            <template #brandModelName="{ item }">
              <td>{{ [item.brand, item.model].filter(Boolean).join(" ") || "—" }}</td>
            </template>

            <template #chasisNo="{ item }">
              <td><small class="text-muted">{{ item.chasisNo || "—" }}</small></td>
            </template>

            <template #customerName="{ item }">
              <td>{{ item.customerName || "—" }}</td>
            </template>

            <template #salesmanName="{ item }">
              <td>{{ item.salesmanName || "—" }}</td>
            </template>

            <template #bodyPrice="{ item }">
              <td class="money">{{ formatMoney(item.bodyPrice) }}</td>
            </template>

            <template #sellingPrice="{ item }">
              <td class="money">{{ formatMoney(item.sellingPrice) }}</td>
            </template>

            <template #grossProfit="{ item }">
              <td class="money" :class="profitClass(grossProfit(item))">
                {{ formatMoney(grossProfit(item)) }}
              </td>
            </template>

            <template #netProfit="{ item }">
              <td class="money" :class="profitClass(netProfit(item))">
                {{ formatMoney(netProfit(item)) }}
              </td>
            </template>

            <template #hpAmount="{ item }">
              <td class="money">{{ formatMoney(item.hpAmount) }}</td>
            </template>

            <template #loanAmount="{ item }">
              <td class="money">{{ formatMoney(item.loanAmount) }}</td>
            </template>

            <template #bankName="{ item }">
              <td><small>{{ item.bankName || "—" }}</small></td>
            </template>

            <template #customerPayments="{ item }">
              <td class="money">
                {{ formatMoney(item.totalCustomerPayments) }}
                <small v-if="item.customerPaymentCount" class="text-muted d-block">
                  {{ item.customerPaymentCount }} pmt{{ item.customerPaymentCount === 1 ? '' : 's' }}
                </small>
              </td>
            </template>

            <template #purchasePayments="{ item }">
              <td class="money">
                {{ formatMoney(item.totalPurchasePayments) }}
                <small v-if="item.purchasePaymentCount" class="text-muted d-block">
                  {{ item.purchasePaymentCount }} pmt{{ item.purchasePaymentCount === 1 ? '' : 's' }}
                </small>
              </td>
            </template>

            <template #adminCostPayments="{ item }">
              <td class="money">
                {{ formatMoney(item.totalAdminCostPayments) }}
                <small v-if="item.adminCostPaymentCount" class="text-muted d-block">
                  {{ item.adminCostPaymentCount }} pmt{{ item.adminCostPaymentCount === 1 ? '' : 's' }}
                </small>
              </td>
            </template>

            <template #expensePayments="{ item }">
              <td class="money">
                {{ formatMoney(item.totalExpensePayments) }}
                <small v-if="item.expensePaymentCount" class="text-muted d-block">
                  {{ item.expensePaymentCount }} pmt{{ item.expensePaymentCount === 1 ? '' : 's' }}
                </small>
              </td>
            </template>

            <template #salesmanCommision="{ item }">
              <td class="money text-danger">{{ formatMoney(item.salesmanCommision) }}</td>
            </template>

            <template #promotionDiscount="{ item }">
              <td class="money">{{ formatMoney(item.promotionDiscount) }}</td>
            </template>

            <template #insuranceAmount="{ item }">
              <td class="money">{{ formatMoney(item.insuranceAmount) }}</td>
            </template>

            <template #othersAmount="{ item }">
              <td class="money">{{ formatMoney(othersAmount(item)) }}</td>
            </template>

            <template #actions="{ item }">
              <td class="text-end">
                <CButton size="sm" color="primary" @click="openDetail(item)">
                  <i class="fas fa-eye me-1"></i> View
                </CButton>
              </td>
            </template>
          </CDataTable>
        </div>
      </CCardBody>

      <CCardFooter v-if="totalPages > 1">
        <CPagination
          :pages="totalPages"
          :active-page.sync="activePage"
          align="center"
        />
      </CCardFooter>
    </CCard>

    <!-- Toasts -->
    <div class="toast-container">
      <CToaster :autohide="3000">
        <CToast
          v-for="(info, idx) in infoList"
          :key="'toast-' + idx + '-' + Date.now()"
          :show="true"
          :header="info.header"
          :color="info.color"
        >{{ info.message }}</CToast>
      </CToaster>
    </div>
  </div>
</template>

<script>
import StockApi from "../../lib/stockApi";
import { formatMalaysiaDate } from "@/utils/dateUtil";

export default {
  name: "SalesReportList",
  data() {
    return {
      api: new StockApi(),
      loading: true,
      loadedItems: [],
      totalItems: 0,
      activePage: 1,
      itemsPerPage: 20,
      filtersExpanded: true,
      showAllColumns: false,
      filters: {
        stockNo: "",
        customerName: "",
        salesmanName: "",
        chasisNo: "",
        brandModelName: "",
      },
      priceMin: "",
      priceMax: "",
      _filterDebounce: null,
      infoList: [],
    };
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.totalItems / this.itemsPerPage));
    },
    coreFields() {
      return [
        { key: "show_index", label: "#", _style: "width:50px" },
        { key: "vehicleRegistrationDate", label: "Reg. Date" },
        { key: "stockNo", label: "Stock No" },
        { key: "brandModelName", label: "Model" },
        { key: "chasisNo", label: "Chassis" },
        { key: "customerName", label: "Customer" },
        { key: "salesmanName", label: "Salesman" },
        { key: "bodyPrice", label: "Body Price", _classes: "text-end" },
        { key: "sellingPrice", label: "Selling Price", _classes: "text-end" },
        { key: "customerPayments", label: "Deposits", _classes: "text-end" },
        { key: "grossProfit", label: "Gross Profit", _classes: "text-end" },
        { key: "loanAmount", label: "Loan", _classes: "text-end" },
        { key: "actions", label: "", _style: "width:90px" },
      ];
    },
    extendedFields() {
      // Inserted between the core columns when "Show all columns" is on.
      // Uses the LIVE payment-bucket sums (totalAdminCostPayments,
      // totalExpensePayments) returned by the sales-report-list endpoint
      // — the legacy per-category flat columns are no longer aggregated
      // here since the bucket is the system of record now.
      return [
        { key: "show_index", label: "#", _style: "width:50px" },
        { key: "vehicleRegistrationDate", label: "Reg. Date" },
        { key: "stockNo", label: "Stock No" },
        { key: "brandModelName", label: "Model" },
        { key: "chasisNo", label: "Chassis" },
        { key: "customerName", label: "Customer" },
        { key: "salesmanName", label: "Salesman" },
        { key: "bodyPrice", label: "Body Price", _classes: "text-end" },
        { key: "sellingPrice", label: "Selling Price", _classes: "text-end" },
        { key: "customerPayments", label: "Deposits", _classes: "text-end" },
        { key: "purchasePayments", label: "Purchase Pmts", _classes: "text-end" },
        { key: "adminCostPayments", label: "Admin Cost Pmts", _classes: "text-end" },
        { key: "expensePayments", label: "Expense Pmts", _classes: "text-end" },
        { key: "hpAmount", label: "HP", _classes: "text-end" },
        { key: "salesmanCommision", label: "Commission", _classes: "text-end" },
        { key: "promotionDiscount", label: "Promotion", _classes: "text-end" },
        { key: "grossProfit", label: "Gross Profit", _classes: "text-end" },
        { key: "netProfit", label: "Net Profit", _classes: "text-end" },
        { key: "loanAmount", label: "Loan", _classes: "text-end" },
        { key: "bankName", label: "Bank" },
        { key: "insuranceAmount", label: "Insurance", _classes: "text-end" },
        { key: "actions", label: "", _style: "width:90px" },
      ];
    },
    visibleFields() {
      return this.showAllColumns ? this.extendedFields : this.coreFields;
    },
  },
  watch: {
    activePage() { this.load(); },
  },
  mounted() { this.load(); },
  methods: {
    load() {
      const self = this;
      self.loading = true;
      const params = {
        page: self.activePage,
        itemsPerPage: self.itemsPerPage,
        filters: { ...self.filters },
        priceMin: self.priceMin,
        priceMax: self.priceMax,
      };
      self.api
        .getSalesReportList(params)
        .then((response) => {
          // Endpoint returns { result: { items, totalItems } } via AutoWrapper.
          const r = (response && response.result) || response || {};
          self.loadedItems = r.items || [];
          self.totalItems = r.totalItems || 0;
        })
        .catch(() => {
          self.toast("Error", "Failed to load sales report", "danger");
          self.loadedItems = [];
          self.totalItems = 0;
        })
        .finally(() => {
          self.loading = false;
        });
    },
    onFilterChange() {
      clearTimeout(this._filterDebounce);
      this._filterDebounce = setTimeout(() => {
        this.activePage = 1;
        this.load();
      }, 350);
    },
    toggleFilters() { this.filtersExpanded = !this.filtersExpanded; },
    openDetail(item) {
      this.$router.push({ name: "salesReportById", params: { id: item.stockId } });
    },

    // ----- Computed-per-row helpers (pure JS, no nested-property crashes) -----
    purchaseCost(item) {
      // Total stock cost = body price + admin costs + (legacy expense flat)
      // Now also reflects payment-bucket sums when present.
      const adminFlat =
        (Number(item.bodyPrice) || 0) +
        (Number(item.insuranceAmount) || 0) +
        (Number(item.roadtaxAmount) || 0) +
        (Number(item.registrationFeeAmount) || 0) +
        (Number(item.puspakomAmount) || 0) +
        (Number(item.transferFeeAmount) || 0);
      // Use the larger of flat sums vs payment-bucket sums (they should
      // be equal after the legacy-sync work, but be defensive).
      const purchasePmts = Number(item.totalPurchasePayments) || 0;
      const expensePmts = Number(item.totalExpensePayments) || 0;
      const adminPmts = Number(item.totalAdminCostPayments) || 0;
      return adminFlat + purchasePmts + expensePmts + adminPmts - (Number(item.bodyPrice) || 0);
      // Subtract bodyPrice once to avoid double-counting (it's in both
      // adminFlat AND can appear in purchase-payment local amounts).
    },
    othersAmount(item) {
      return (
        (Number(item.puspakomAmount) || 0) +
        (Number(item.transferFeeAmount) || 0) +
        (Number(item.registrationFeeAmount) || 0) +
        (Number(item.roadtaxAmount) || 0)
      );
    },
    grossProfit(item) {
      const selling = Number(item.sellingPrice) || 0;
      const cost = this.purchaseCost(item);
      return selling - cost;
    },
    netProfit(item) {
      const gp = this.grossProfit(item);
      const comm = Number(item.salesmanCommision) || 0;
      const promo = Number(item.promotionDiscount) || 0;
      return gp - comm - promo;
    },
    profitClass(v) {
      if (v > 0) return "text-success fw-bold";
      if (v < 0) return "text-danger fw-bold";
      return "text-muted";
    },

    // ----- Formatting helpers -----
    formatMoney(n) {
      const v = Number(n) || 0;
      return v.toLocaleString("en-MY", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    formatDate(d) {
      if (!d) return "—";
      const out = formatMalaysiaDate(d);
      return out === "—" ? "—" : (out || "—");
    },

    // ----- CSV export -----
    exportCSV() {
      const headers = [
        "No", "Reg. Date", "Stock No", "Model", "Chassis", "Customer",
        "Salesman", "Body Price", "Selling Price", "Deposits Received",
        "Purchase Payments", "Admin Cost Payments", "Expense Payments",
        "HP", "Commission", "Promotion", "Gross Profit", "Net Profit",
        "Loan", "Bank", "Insurance",
      ];
      const escape = (v) => `"${String(v == null ? "" : v).replace(/"/g, '""')}"`;
      const rows = this.loadedItems.map((item, i) => [
        (this.activePage - 1) * this.itemsPerPage + i + 1,
        this.formatDate(item.vehicleRegistrationDate),
        item.stockNo || "",
        [item.brand, item.model].filter(Boolean).join(" "),
        item.chasisNo || "",
        item.customerName || "",
        item.salesmanName || "",
        this.formatMoney(item.bodyPrice),
        this.formatMoney(item.sellingPrice),
        this.formatMoney(item.totalCustomerPayments),
        this.formatMoney(item.totalPurchasePayments),
        this.formatMoney(item.totalAdminCostPayments),
        this.formatMoney(item.totalExpensePayments),
        this.formatMoney(item.hpAmount),
        this.formatMoney(item.salesmanCommision),
        this.formatMoney(item.promotionDiscount),
        this.formatMoney(this.grossProfit(item)),
        this.formatMoney(this.netProfit(item)),
        this.formatMoney(item.loanAmount),
        item.bankName || "",
        this.formatMoney(item.insuranceAmount),
      ].map(escape).join(","));
      const csv = "﻿" + [headers.join(","), ...rows].join("\r\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `sales_report_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      this.toast("Success", `Exported ${this.loadedItems.length} rows`, "success");
    },

    toast(header, message, color) {
      this.infoList.push({ header, message, color });
    },
  },
};
</script>

<style scoped>
/* ===== Sales Report — unified typography ===== */
/* The same style block lives in SalesReport.vue so both pages render
   with identical fonts, sizes, weights, and color tokens. Keep them in
   sync when editing. */
.sales-report-page {
  padding: 1rem;
  background: #f3f4f7;
  min-height: 100vh;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  font-size: 0.875rem;
  color: #2d3748;
  line-height: 1.5;
}

.sales-report-page h4 {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1c2742;
  margin: 0;
  letter-spacing: -0.2px;
}

.sales-report-page small {
  font-size: 0.8rem;
  color: #768192;
}

.action-bar {
  border-left: 4px solid #321fdb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.section-header {
  background: #f8f9fa;
  border-bottom: 2px solid #321fdb;
  color: #321fdb;
  padding: 0.55rem 1rem;
  font-size: 0.9rem;
  font-weight: 600;
}

.section-header strong {
  font-size: 0.95rem;
  font-weight: 700;
}

.results-card,
.filter-card {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.sales-report-page >>> .form-control,
.sales-report-page >>> .form-label,
.sales-report-page >>> label {
  font-family: inherit;
  font-size: 0.85rem;
}

.sales-report-page >>> label {
  font-weight: 600;
  color: #4f5d73;
  margin-bottom: 0.25rem;
}

.sales-report-table >>> td.money,
.sales-report-table >>> th.text-end {
  text-align: right;
  font-family: "Courier New", Consolas, monospace;
  font-size: 0.85rem;
}

.sales-report-table >>> th {
  background: #f8f9fa;
  color: #2d3748;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.7rem;
  letter-spacing: 0.5px;
  border-bottom: 2px solid #321fdb;
  padding: 0.5rem 0.75rem;
}

.sales-report-table >>> td {
  font-size: 0.85rem;
  padding: 0.5rem 0.75rem;
  vertical-align: middle;
}

.sales-report-table >>> tr:hover {
  background: #f0f3ff;
}

.toast-container {
  position: fixed;
  top: 1.5rem;
  right: 1.5rem;
  z-index: 1050;
}
</style>
